import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Textarea,
  Dropdown,
  Option,
  Spinner,
  Text,
  Divider,
  Field,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
  Switch,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Filled,
  DismissCircle24Regular,
  OpenRegular,
} from '@fluentui/react-icons';
import { useMutation } from '@tanstack/react-query';
import { BoondSearchResult } from '@gridwise/shared';
import { createAction } from '../api/actions';
import BoondSearchInput from '../components/BoondSearchInput';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
    maxWidth: '560px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  sourceBox: {
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  excerpt: {
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    paddingLeft: tokens.spacingHorizontalM,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    justifyContent: 'flex-end',
    paddingTop: tokens.spacingVerticalM,
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '64px',
  },
});

interface TeamsMessageData {
  messageId: string;
  messageUrl: string;
  teamId: string;
  channelId: string;
  authorId: string;
  authorName: string;
  date: string;
  excerpt: string;
}

function parseTeamsContext(): TeamsMessageData | null {
  // In real Teams extension context, this comes from the task module parameters
  const params = new URLSearchParams(window.location.search);
  const messageId = params.get('messageId') || 'mock-msg-001';
  const messageUrl = params.get('messageUrl') || 'https://teams.microsoft.com/mock';
  const teamId = params.get('teamId') || 'mock-team';
  const channelId = params.get('channelId') || 'mock-channel';
  const authorId = params.get('authorId') || 'mock-user';
  const authorName = params.get('authorName') || 'Utilisateur Teams';
  const date = params.get('date') || new Date().toISOString();
  const excerpt = params.get('excerpt') || 'Message Teams sélectionné';

  return { messageId, messageUrl, teamId, channelId, authorId, authorName, date, excerpt };
}

function parsePreselectedEntity() {
  const params = new URLSearchParams(window.location.search);
  return {
    entityType: params.get('entityType') as any,
    entityId: params.get('entityId') || '',
    entityLabel: params.get('entityLabel') || '',
  };
}

export default function EnvoiBoond() {
  const styles = useStyles();
  const teamsMsg = parseTeamsContext();
  const preselected = parsePreselectedEntity();

  const [entity, setEntity] = useState<{ type: string; id: string; label: string } | null>(
    preselected.entityId ? { type: preselected.entityType, id: preselected.entityId, label: preselected.entityLabel } : null,
  );
  const [actionType, setActionType] = useState('followup');
  const [actionTitle, setActionTitle] = useState('');
  const [actionSummary, setActionSummary] = useState(teamsMsg?.excerpt || '');
  const [responsible, setResponsible] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [createPlanner, setCreatePlanner] = useState(false);
  const [notifyTeams, setNotifyTeams] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createAction,
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || 'Erreur lors de la création');
    },
  });

  const handleEntitySelect = (result: BoondSearchResult) => {
    setEntity({ type: result.type, id: result.id, label: result.label });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!entity) return setError('Veuillez sélectionner une entité Boond (société, contact, opportunité ou projet).');
    if (!actionTitle.trim()) return setError('Veuillez saisir un titre pour l\'action.');
    if (!responsible.trim()) return setError('Veuillez indiquer un responsable.');

    mutation.mutate({
      teamsMessageId: teamsMsg?.messageId || '',
      teamsMessageUrl: teamsMsg?.messageUrl || '',
      teamsTeamId: teamsMsg?.teamId || '',
      teamsChannelId: teamsMsg?.channelId || '',
      teamsAuthorId: teamsMsg?.authorId || '',
      teamsAuthorName: teamsMsg?.authorName,
      teamsDate: teamsMsg?.date || new Date().toISOString(),
      teamsExcerpt: teamsMsg?.excerpt || '',
      boondEntityType: entity.type as any,
      boondEntityId: entity.id,
      boondEntityLabel: entity.label,
      actionType,
      actionTitle,
      actionSummary,
      actionResponsible: responsible,
      actionDueDate: dueDate || undefined,
      actionPriority: priority,
      createPlannerTask: createPlanner,
      notifyInTeams: notifyTeams,
    });
  };

  // Success state
  if (mutation.isSuccess) {
    const result = mutation.data;
    return (
      <div className={styles.container}>
        <div className={styles.success}>
          <CheckmarkCircle24Filled className={styles.successIcon} style={{ fontSize: 64 }} />
          <Text size={500} weight="semibold">Action créée dans Boond !</Text>
          <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
            {result.confirmationMessage}
          </Text>
          <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
            <Button
              appearance="primary"
              icon={<OpenRegular />}
              as="a"
              href={`${import.meta.env.VITE_BOOND_APP_URL || '#'}/action/${result.boondActionId}`}
              target="_blank"
            >
              Ouvrir dans Boond
            </Button>
            <Button
              appearance="secondary"
              onClick={() => mutation.reset()}
            >
              Créer une autre action
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form className={styles.container} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <Text size={500} weight="semibold">Envoyer vers Boond</Text>
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
          Transformez ce message Teams en action Boond structurée
        </Text>
      </div>

      {/* Source Teams */}
      {teamsMsg && (
        <div className={styles.section}>
          <Text size={200} weight="semibold" className={styles.sectionTitle}>Message source</Text>
          <div className={styles.sourceBox}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {teamsMsg.authorName} · {new Date(teamsMsg.date).toLocaleString('fr-FR')}
            </Text>
            <Text size={300} className={styles.excerpt}>"{teamsMsg.excerpt}"</Text>
          </div>
        </div>
      )}

      <Divider />

      {/* Rattachement Boond */}
      <div className={styles.section}>
        <Text size={200} weight="semibold" className={styles.sectionTitle}>Rattachement Boond</Text>
        <Field label="Société, contact, opportunité ou projet *">
          {entity ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Text size={300} weight="semibold">{entity.label}</Text>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>({entity.type})</Text>
              <Button
                size="small"
                appearance="subtle"
                icon={<DismissCircle24Regular />}
                onClick={() => setEntity(null)}
              />
            </div>
          ) : (
            <BoondSearchInput
              placeholder="Rechercher dans Boond..."
              onSelect={handleEntitySelect}
            />
          )}
        </Field>
      </div>

      <Divider />

      {/* Action */}
      <div className={styles.section}>
        <Text size={200} weight="semibold" className={styles.sectionTitle}>Action à créer</Text>
        <div className={styles.twoCol}>
          <Field label="Type d'action *">
            <Dropdown
              value={actionType}
              onOptionSelect={(_, d) => setActionType(d.optionValue || 'followup')}
            >
              <Option value="call">Appel</Option>
              <Option value="email">Email</Option>
              <Option value="meeting">Rendez-vous</Option>
              <Option value="followup">Relance</Option>
              <Option value="decision">Décision</Option>
              <Option value="note">Note</Option>
              <Option value="risk">Risque</Option>
              <Option value="project-action">Action projet</Option>
              <Option value="other">Autre</Option>
            </Dropdown>
          </Field>
          <Field label="Priorité">
            <Dropdown
              value={priority}
              onOptionSelect={(_, d) => setPriority(d.optionValue || 'medium')}
            >
              <Option value="low">Faible</Option>
              <Option value="medium">Normale</Option>
              <Option value="high">Élevée</Option>
              <Option value="urgent">Urgente</Option>
            </Dropdown>
          </Field>
        </div>
        <Field label="Titre / Objet *">
          <Input
            value={actionTitle}
            onChange={(_, d) => setActionTitle(d.value)}
            placeholder="Ex : Relancer suite à échange Teams du 13/05"
          />
        </Field>
        <Field label="Résumé">
          <Textarea
            value={actionSummary}
            onChange={(_, d) => setActionSummary(d.value)}
            rows={3}
            placeholder="Contexte, décisions, points importants..."
          />
        </Field>
        <div className={styles.twoCol}>
          <Field label="Responsable *">
            <Input
              value={responsible}
              onChange={(_, d) => setResponsible(d.value)}
              placeholder="Prénom Nom"
            />
          </Field>
          <Field label="Échéance">
            <Input
              type="date"
              value={dueDate}
              onChange={(_, d) => setDueDate(d.value)}
            />
          </Field>
        </div>
      </div>

      <Divider />

      {/* Options */}
      <div className={styles.section}>
        <Text size={200} weight="semibold" className={styles.sectionTitle}>Options</Text>
        <Switch
          checked={createPlanner}
          onChange={(_, d) => setCreatePlanner(d.checked)}
          label="Créer également une tâche Microsoft Planner"
        />
        <Switch
          checked={notifyTeams}
          onChange={(_, d) => setNotifyTeams(d.checked)}
          label="Notifier dans Teams après création"
        />
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.actions}>
        <Button appearance="secondary" type="button" onClick={() => window.close()}>
          Annuler
        </Button>
        <Button
          appearance="primary"
          type="submit"
          disabled={mutation.isPending}
          icon={mutation.isPending ? <Spinner size="tiny" /> : undefined}
        >
          {mutation.isPending ? 'Création...' : 'Créer dans Boond'}
        </Button>
      </div>
    </form>
  );
}
